import { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { change, Field, reduxForm } from 'redux-form';

import { beGetSalonTaxesAndFees } from 'actions/salon/beGetSalonTaxesAndFees.action';
import Button from 'components/form/Button.component';
import { TaxesAndFeesSection } from 'components/taxes/TaxesAndFeesSection/TaxesAndFeesSection.component';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import { useTranslation, withTranslation } from 'providers/GILT.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import asObject from 'utils/objects/asObject.util';
import isEmptyString from 'utils/predicates/string/isEmptyString.util';
import parsfixed from 'utils/values/parsfixed.util';

import { addProduct, editProduct, resetSavedProperty } from 'settings/product-listing/actions';
import SavingStatus from 'settings/product-listing/components/SavingStatus';

import 'styles/common/confirmation-modal.scss';
import CN from './ProductModal.module.scss';

const InputField = ({ input, meta, ...rest }) => {
  const { name, value } = asObject(input);
  const { touched, error, onFocus, active, form, dispatch } = asObject(meta);

  const className = `${touched && error && !active ? 'has-error' : ''}`;
  const resetField = () => void dispatch(change(form, name, ''));

  return (
    <div data-bem="InputField--settings">
      <div className="input-clear-container">
        <input className={`${className} utl-full-width`} onFocus={onFocus} {...input} {...rest} />

        {value !== '' && (
          // NOTE: This span clears the field, but it's not visible / no styles
          <span className="clear-value animated bounceIn" onClick={resetField} role="button" tabIndex="0" />
        )}
      </div>

      <div className="input-prompt-show">
        <div className="input-baseline" />
        <span>{touched ? error : ''}</span>
      </div>
    </div>
  );
};

const ProductFormWithSubmit = ({ addProduct, formType, editProduct, productId, salonId, cancel, reset, productsList, resetSavedProperty, handleSubmit, invalid, submitting }) => {
  const { t } = useTranslation();
  const [selectedTaxes, setSelectedTaxes] = useState({});
  const [selectedFees, setSelectedFees] = useState({});
  const [isTaxExempt, setIsTaxExempt] = useState(false);
  const [taxExemptReason, setTaxExemptReason] = useState('');

  const { qj: taxesAndFees, isLoading } = usePrepareReadOnly(beGetSalonTaxesAndFees);
  const taxes = useMemo(() => taxesAndFees?.taxes ?? [], [taxesAndFees]);
  const fees = useMemo(() => taxesAndFees?.fees ?? [], [taxesAndFees]);

  const onSubmit = useCallback(
    (values) => {
      const { title, brand, sub_brand: subBrand, price, barcode } = values;
      // TODO: `formType` is hardcoded to `create` in the parent, everything related to edit should be removed.
      if (formType === 'edit') {
        return void editProduct(values, productId);
      }

      if (formType !== 'create') return;

      if (isTaxExempt && !taxExemptReason?.trim()) {
        return;
      }

      const productTaxes = taxes.filter((tax) => tax.applied_to === 'products' || tax.applied_to === 'products_and_services');
      const productFees = fees.filter((fee) => fee.applied_to === 'products' || fee.applied_to === 'products_and_services');

      const defaultTaxes = productTaxes.filter((tax) => tax.is_default);
      const taxIdsSet = new Set();
      defaultTaxes.forEach((tax) => {
        if (selectedTaxes[tax.id] === undefined) {
          taxIdsSet.add(tax.id);
        }
      });
      const selectedTaxesIds = Object.entries(selectedTaxes)
        .filter(([_, value]) => value)
        .map(([key]) => parseInt(key));
      selectedTaxesIds.forEach((taxId) => taxIdsSet.add(taxId));
      const finalTaxes = Array.from(taxIdsSet);

      const defaultFees = productFees.filter((fee) => fee.is_default);
      const feeIdsSet = new Set();
      defaultFees.forEach((fee) => {
        if (selectedFees[fee.id] === undefined) {
          feeIdsSet.add(fee.id);
        }
      });
      const selectedFeesIds = Object.entries(selectedFees)
        .filter(([_, value]) => value)
        .map(([key]) => parseInt(key));
      selectedFeesIds.forEach((feeId) => feeIdsSet.add(feeId));
      const finalFees = Array.from(feeIdsSet);

      const quantity = parsfixed(values.quantityInStock);
      return void addProduct(salonId, title, brand, subBrand, price && parsfixed(price), quantity, barcode, finalTaxes, finalFees, isTaxExempt, taxExemptReason);
    },
    [formType, taxes, fees, selectedTaxes, selectedFees, addProduct, salonId, isTaxExempt, taxExemptReason, editProduct, productId],
  );

  const { isPosting, isSaved } = asObject(productsList);

  const isTaxInvalid = isTaxExempt && !taxExemptReason?.trim();
  const buttonName = useMemo(() => {
    if (isPosting) return t('common.saving');
    if (isSaved) return t('common.saved');

    return formType === 'edit' ? t('stocks.managerLayout.addProductModal.editItemButton') : t('stocks.managerLayout.addProductModal.addItemButton');
  }, [formType, isPosting, isSaved, t]);

  // TODO: replace and use SalonModal component instead this custom modal implementation
  return (
    <div data-bem="ProductModal">
      <div
        className={cn({
          [CN.scrollContainer]: true,
          'modal-content': true,
        })}
      >
        <div className="row form-horizontal">
          <div className="col-sm-7">
            <label className={CN.label} htmlFor="title">
              {t('stocks.managerLayout.addProductModal.productName')}
            </label>
            <Field name="title" placeholder={t('stocks.managerLayout.addProductModal.productName')} type="text" component={InputField} />
          </div>
          <div className={CN.row}>
            <div className={CN.fieldContainer}>
              <label className={CN.label} htmlFor="brand">
                {t('stocks.managerLayout.addProductModal.brand')}
              </label>
              <Field name="brand" type="text" component={InputField} />
            </div>

            <div className={CN.fieldContainer}>
              <label className={CN.label} htmlFor="sub_brand">
                {t('stocks.managerLayout.addProductModal.subBrand')}
              </label>
              <Field name="sub_brand" type="text" component={InputField} />
            </div>
          </div>
          <div className={CN.row}>
            <div className={CN.fieldContainer}>
              <label className={CN.label} htmlFor="price">
                {t('stocks.managerLayout.addProductModal.retailPrice')}
              </label>
              <Field name="price" type="number" min="0" step="0.01" placeholder="0.00" component={InputField} />
            </div>

            <div className={CN.fieldContainer}>
              <label className={CN.label} htmlFor="quantityInStock">
                {t('stocks.managerLayout.addProductModal.stockQuantity')}
              </label>
              <Field name="quantityInStock" type="number" min="0" placeholder={t('stocks.managerLayout.addProductModal.stockQuantity')} component={InputField} />
            </div>
          </div>

          <div className="col-sm-7">
            <label className={CN.label} htmlFor="barcode">
              {t('stocks.managerLayout.addProductModal.barcode')}
            </label>
            <Field name="barcode" type="text" component={InputField} />
          </div>

          <TaxesAndFeesSection
            location="product"
            taxes={taxes}
            fees={fees}
            isLoading={isLoading}
            onSelectTax={setSelectedTaxes}
            onSelectFee={setSelectedFees}
            onToggleExempt={setIsTaxExempt}
            onChangeReason={setTaxExemptReason}
          />

          <SavingStatus productsList={productsList} cancel={cancel} reset={reset} resetSavedProperty={resetSavedProperty} />

          <p className="add-product-notes" />
        </div>
      </div>

      <div className="modal-footer">
        <Button variant="outlineForce" onClick={cancel}>
          {t('common.buttons.cancel')}
        </Button>

        <Button variant="primaryForce" onClick={handleSubmit(onSubmit)} disabled={invalid || submitting || isPosting || isTaxInvalid}>
          {buttonName}
        </Button>
      </div>
    </div>
  );
};

const validate = (values, props) => {
  const { t } = props;
  const errors = {};

  if (!values.title || fromAnyToTrimmed(values.title).length < 3) {
    errors.title = t('stocks.managerLayout.addProductModal.productNameErrMsg');
  }

  if (!values.brand || !fromAnyToTrimmed(values.brand)) {
    errors.brand = t('stocks.managerLayout.addProductModal.brandErrMsg');
  }

  if (isEmptyString(values.price)) {
    errors.price = t('stocks.managerLayout.addProductModal.priceRequiredErrMsg');
  } else if (isNaN(values.price)) {
    errors.price = t('stocks.managerLayout.addProductModal.priceInvalidErrMsg');
  } else if (parseFloat(values.price) < 0) {
    errors.price = t('stocks.managerLayout.addProductModal.pricePositiveErrMsg');
  }

  if (isEmptyString(values.quantityInStock)) {
    errors.quantityInStock = t('stocks.managerLayout.addProductModal.quantityRequiredErrMsg');
  } else if (isNaN(values.quantityInStock)) {
    errors.quantityInStock = t('stocks.managerLayout.addProductModal.quantityInvalidErrMsg');
  } else if (parseFloat(values.quantityInStock) < 0) {
    errors.quantityInStock = t('stocks.managerLayout.addProductModal.quantityPositiveErrMsg');
  }

  return errors;
};

const mapStateToProps = (state) => ({
  productsList: state?.settings?.products,
});

const mapDispatchToProps = (dispatch) => ({
  addProduct: (salonId, title, brand, sub_brand, price, barcode, quantityInStock, selectedTaxes, selectedFees, isTaxExempt, taxExemptReason) =>
    dispatch(addProduct(salonId, title, brand, sub_brand, price, barcode, quantityInStock, selectedTaxes, selectedFees, isTaxExempt, taxExemptReason)),
  resetSavedProperty: () => dispatch(resetSavedProperty()),
  editProduct: (product, productId) => dispatch(editProduct(product, productId)),
});

const ProductFormWithReduxForm = reduxForm({
  validate,
  form: 'ProductForm',
})(ProductFormWithSubmit);

const ProductForm = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ProductFormWithReduxForm));

const ProductModal = ({ cancel, formType, popupTitle, product, productId, salonId }) => (
  <div data-bem="ProductModal">
    <div className="confirmation-modal animated zoomIn">
      <div className="modal-body">
        <h2 className="modal-title">{popupTitle}</h2>

        <ProductForm initialValues={product || null} productId={productId || null} cancel={cancel} salonId={salonId} formType={formType} />
      </div>
    </div>
  </div>
);

ProductModal.defaultProps = {
  product: null,
  productId: 0,
};

// this extra export is needed for tests that provide their own mock for connect()
export { InputField as InputField4Test };
export { ProductFormWithReduxForm as ProductForm4Test };

export default ProductModal;
